import { forwardRef } from 'react';
import type { ReactNode, PropsWithoutRef, Ref } from 'react';
import styled, { css } from 'styled-components';

import type { BaseProps, ForwardRefForwardPropsComponent, NoChildrenProp } from '../../types';
import { defaultThemeProp } from '../../theme';
import { calculateFontSize } from '../../styles';
import Button from '../Button';
import Text from '../Text';
import { useI18n } from '../../hooks';

export interface ListProps extends BaseProps, NoChildrenProp {
  /** List content. */
  items: ReactNode[];
  /** List Heading. */
  heading?: string;
  /** Number of list items that need to be shown. */
  count?: number;
  /** Callback to display more/less items. */
  onToggleShow?: () => void;
  /** Ref forwarded to the wrapping element. */
  ref?: Ref<HTMLDivElement>;
}

export interface VisualListProps extends ListProps {
  /** Type of list. */
  variant: 'ordered' | 'unordered';
  /** Depth of list (used for recursion). */
  depth?: number;
}

const olTypes = ['decimal', 'lower-alpha', 'lower-roman'] as const;
const ulTypes = ['disc', 'circle', 'square'] as const;

export const StyledList = styled.div<{
  depth: number;
  listType: (typeof olTypes)[number] | (typeof ulTypes)[number];
}>(({ theme, depth, listType }) => {
  const fontSize = calculateFontSize(theme.base['font-size'], theme.base['font-scale']);

  return css`
    padding-inline-start: calc(2 * ${theme.base.spacing});

    h3 {
      margin-block-end: calc(2 * ${theme.base.spacing});
    }

    & > ol,
    & > ul {
      list-style-type: ${listType};

      & > li {
        &:not(:last-child) {
          margin-block-end: calc(0.5 * ${theme.base.spacing});
        }

        ${depth > 0
          ? css`
              font-size: ${fontSize.xs};
            `
          : css`
              &::marker {
                font-weight: ${theme.base['font-weight'].bold};
              }
            `}
      }

      ${depth === 0 &&
      css`
        padding-inline-start: calc(2 * ${theme.base.spacing});
      `}
    }

    & & > ol,
    & & > ul {
      padding-inline-start: 0;
    }
  `;
});

StyledList.defaultProps = defaultThemeProp;

export const StyledSublistItem = styled.li`
  list-style: none;
`;

const List: ForwardRefForwardPropsComponent<VisualListProps> = forwardRef(function ListFunction(
  {
    items,
    variant,
    count,
    onToggleShow,
    depth = 0,
    heading,
    ...restProps
  }: PropsWithoutRef<VisualListProps>,
  ref: VisualListProps['ref']
) {
  const t = useI18n();
  const listContent = items.map((value, idx) =>
    Array.isArray(value) ? (
      // eslint-disable-next-line react/no-array-index-key
      <StyledSublistItem key={idx}>
        <List depth={depth + 1} variant={variant} items={value} />
      </StyledSublistItem>
    ) : (
      // eslint-disable-next-line react/no-array-index-key
      <li key={idx}>{value}</li>
    )
  );

  return (
    <StyledList
      {...restProps}
      ref={ref}
      depth={depth}
      listType={(variant === 'ordered' ? olTypes : ulTypes)[depth % 3]}
    >
      {heading && <Text variant='h3'>{heading}</Text>}
      {variant === 'ordered' ? <ol>{listContent}</ol> : <ul>{listContent}</ul>}
      {count && onToggleShow && (
        <Button variant='link' onClick={onToggleShow}>
          {count > items.flat().length ? t('show_more') : t('show_less')}
        </Button>
      )}
    </StyledList>
  );
});

export default List;
